import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';

import PostPaymentFormAnswer from '../CoachTools/Model/PostPaymentFormAnswer';

const CONSENT_QUESTION_ID = 'contentSharingConsent';

// Coaches word the question differently, so any of these counts as asking it.
const CONSENT_QUESTION_TITLES = [
  'Are you comfortable with your progress photos being used for promotional materials (social media, website, etc.)?',
  'Are you okay with me highlighting your transformation photos on my social media? (no face will be shown)',
];

/*
  The second wording carries the condition in the question itself, so agreeing to it is agreeing to the
  face being kept out — whichever of its answers the client picked. The condition is a property of that
  question, not of the answer text, which is why it is matched separately below.
*/
const [, NO_FACE_QUESTION_TITLE] = CONSENT_QUESTION_TITLES;

const CONSENT_AFFIRMATIVE_ANSWER = 'yes';
const CONSENT_FACE_BLURRED_ANSWER = 'yes, if my face is blurred';
const CONSENT_ASK_EACH_TIME_ANSWER = 'ask me each time before use';

const normalise = (value) => String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const NORMALISED_QUESTION_TITLES = CONSENT_QUESTION_TITLES.map(normalise);
const NORMALISED_NO_FACE_QUESTION_TITLE = normalise(NO_FACE_QUESTION_TITLE);

const isConsentQuestion = (answer) => {
  const key = answer?.custom_key ?? answer?.key;
  return key === CONSENT_QUESTION_ID || NORMALISED_QUESTION_TITLES.includes(normalise(answer?.title));
};

/*
  The question is single-choice, so `value` is a string and the array branch is only defensive. Any yes
  allows, the same rule the loop over answers below applies: a form that somehow carries a yes next to
  another selection is still a yes. Everything else fails closed — including "Ask me each time before
  use", which is an agreement to be asked, not to be posted. That answer never allows on its own; it is
  reported separately as `requiresClientApproval`, and the highlight then waits for the client to agree
  in the app before the coach ever sees it.
*/
const selectionsOf = (value) => (Array.isArray(value) ? value : [value]).map(normalise);

const isAffirmative = (value) => selectionsOf(value)
  .some((one) => one === CONSENT_AFFIRMATIVE_ANSWER || one === CONSENT_FACE_BLURRED_ANSWER);

/** Whether the client asked to be asked. Never a yes: it only opens the path that asks them in the app. */
const isAskEachTimeAnswer = (value) => selectionsOf(value).includes(CONSENT_ASK_EACH_TIME_ANSWER);

/** Whether that yes came with the face condition attached. Only ever read alongside `isAffirmative`. */
const isFaceBlurredAnswer = (value) => selectionsOf(value).includes(CONSENT_FACE_BLURRED_ANSWER);

/** Whether the question itself promised the face would be kept out, whatever the client answered. */
const isNoFaceQuestion = (answer) => normalise(answer?.title) === NORMALISED_NO_FACE_QUESTION_TITLE;

/*
  The cache exists so a feed of check-in cards does not fire the same query per card — a seconds-scale
  problem, which is why the window is short rather than the whole page load. A client who submits the
  form mid-session then stops being blocked within a minute instead of at the next reload (fail-closed
  either way, but a stale ✕ reads as the feature being broken). When client-side withdrawal ships this
  window is also the ceiling on how long a withdrawn answer keeps allowing highlights, and the submit
  guard in ShareAsHighlightModal has to bypass the cache outright.
*/
const CONSENT_TTL_MS = 60000;

const consentByUserId = new Map();

const NO_CONSENT = { isAllowed: false, requiresFaceBlur: false, requiresClientApproval: false };

const readConsent = async (userId) => {
  /*
    Only a submission that carried the question speaks to consent, and among those the newest wins:
    a later form that never asked cannot revoke an earlier yes, while a later one that asked again
    and got a no does. Documents in the legacy `responses` string format predate the question.
  */
  const answerDocs = await PostPaymentFormAnswer.getAllByUser(userId);
  const latestAsked = answerDocs.find(
    (doc) => Array.isArray(doc.answers) && doc.answers.some(isConsentQuestion),
  );
  if (!latestAsked) {
    return NO_CONSENT;
  }

  // Any yes allows, so a form carrying both wordings is not decided by whichever comes first.
  const consentAnswers = latestAsked.answers.filter(isConsentQuestion);
  const isAllowed = consentAnswers.some((answer) => isAffirmative(answer.value));
  return {
    isAllowed,
    // Either the answer attached the condition or the question already carried it. Erring towards the
    // warning: a form carrying both wordings warns on the strength of the stricter one.
    requiresFaceBlur: consentAnswers.some(
      (answer) => isFaceBlurredAnswer(answer.value) || isNoFaceQuestion(answer),
    ),
    // Only when nothing on that submission already allows: a yes needs no asking.
    requiresClientApproval: !isAllowed && consentAnswers.some((answer) => isAskEachTimeAnswer(answer.value)),
  };
};

const resolveContentSharingConsent = (userId) => {
  if (!userId) {
    return Promise.resolve(NO_CONSENT);
  }
  const cached = consentByUserId.get(userId);
  if (cached && Date.now() - cached.at < CONSENT_TTL_MS) {
    return cached.promise;
  }

  const entry = { at: Date.now() };
  entry.promise = readConsent(userId).catch((error) => {
    Sentry.captureException(error, {
      extra: { message: 'Failed to resolve content sharing consent', userId },
    });
    // Drop only our own entry, so a retry that already replaced it is not thrown away.
    if (consentByUserId.get(userId) === entry) {
      consentByUserId.delete(userId);
    }
    return NO_CONSENT;
  });
  consentByUserId.set(userId, entry);
  return entry.promise;
};

const useContentSharingConsent = (userId) => {
  const [consent, setConsent] = useState({ ...NO_CONSENT, isReady: false });

  useEffect(() => {
    if (!userId) {
      setConsent({ ...NO_CONSENT, isReady: true });
      return undefined;
    }

    let isActive = true;
    setConsent({ ...NO_CONSENT, isReady: false });
    resolveContentSharingConsent(userId).then((result) => {
      if (isActive) {
        setConsent({ ...result, isReady: true });
      }
    });
    return () => { isActive = false; };
  }, [userId]);

  return consent;
};

export default useContentSharingConsent;
// The pure rules are exported for the tests that pin the fail-closed behaviour.
export {
  resolveContentSharingConsent,
  isConsentQuestion,
  isAffirmative,
  isFaceBlurredAnswer,
  isAskEachTimeAnswer,
  isNoFaceQuestion,
  CONSENT_QUESTION_ID,
  CONSENT_QUESTION_TITLES,
};
